/**
 * Reusable confirmation modal for destructive admin actions (delete group,
 * delete user, remove selected, etc.). Enforces the requirement 19.8 rule
 * that every destructive action goes through a modal before firing the
 * DELETE request.
 *
 * Props:
 *   open       — whether the modal is visible
 *   title      — short heading ("Delete group?")
 *   message    — body text or JSX
 *   confirmLabel / cancelLabel — button labels (sensible defaults)
 *   confirmType — Meridian button type; use "secondary" for destructive
 *                 actions so Meridian renders the red variant
 *   onConfirm  — callback when the user confirms
 *   onCancel   — callback when the user cancels (or closes)
 *   busy       — disables buttons while an async confirm action runs
 */

import React from 'react';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Button from '@amzn/meridian/button';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmType?: 'primary' | 'secondary' | 'tertiary';
  destructive?: boolean;
  busy?: boolean;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmType = 'primary',
  destructive = false,
  busy = false,
  onConfirm,
  onCancel,
}) => {
  return (
    <Modal open={open} title={title} onClose={onCancel} scrollContainer="modal" aria-describedby="admin-confirm-body">
      <div id="admin-confirm-body">
        {typeof message === 'string' ? <Text>{message}</Text> : message}
      </div>
      <ModalFooter>
        <Row alignmentHorizontal="end" widths="fit">
          <Button type="tertiary" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button
            type={destructive ? 'secondary' : confirmType}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? 'Working…' : confirmLabel}
          </Button>
        </Row>
      </ModalFooter>
    </Modal>
  );
};
